import moment from 'moment';

import { getCurrentYear, fromIgnoredUtc } from '@utils/date';
import {
  FREQUENCIES,
  TIME_FRAME,
  TIME_FRAME_TITLES,
  ADDON_TITLES
} from '@services/minte';

export function stripePriceConverter(price) {
  const newPrice = price / 100;
  if (isNaN(newPrice)) {
    return 0;
  }
  return newPrice.toFixed(2);
}

function objectConverter(data, converter) {
  return (data || []).reduce((acc, item) => {
    acc[item.name] = converter(item);
    return acc;
  }, {});
}

export function transformFrequencyData(frequency) {
  const { name, title, shortTitle, shortDescription, ...rest } = frequency;

  let icon;

  switch (name) {
    case FREQUENCIES.ONCE:
      icon = 'single-occurence';
      break;
    case FREQUENCIES.REGULARLY:
      icon = 'recurring-occurence';
      break;
  }

  return {
    ...rest,
    name,
    title: shortTitle,
    fullTitle: title,
    subtitle: shortDescription,
    icon
  };
}

export function transformServiceTypeData(serviceType) {
  const { name, ...rest } = serviceType;
  const { title } = rest;
  return {
    ...rest,
    name,
    fullTitle: title,
    icon: name
  };
}

export function tranformAddonData(addon) {
  const { name, ...rest } = addon;
  return {
    ...rest,
    name,
    icon: name,
    title: ADDON_TITLES[name]
  };
}

export function transformInitialAppData(appData) {
  if (!appData) {
    return;
  }

  const newData = Object.keys(appData).reduce((acc, key) => {
    const data = appData[key];
    switch (key) {
      case 'frequencies':
        acc[key] = objectConverter(data, transformFrequencyData);
        break;
      case 'serviceTypes':
        acc[key] = objectConverter(data, transformServiceTypeData);
        break;
      case 'addons':
        acc[key] = objectConverter(data, tranformAddonData);
        break;
      default:
        acc[key] = data;
        break;
    }
    return acc;
  }, {});

  // add pricing data
  const { properties } = appData;
  newData.properties = properties;

  return newData;
}

export function transformTransaction(transactions) {
  if (!transactions) {
    return null;
  }

  const transaction =
    (Array.isArray(transactions) ? transactions[0] : transactions) || {};

  const { updatedAt, price } = transaction;

  const momentTransactionDate = fromIgnoredUtc(moment.utc(updatedAt));
  const dateFormatStr = 'MMM DD, YYYY';

  return {
    ...transaction,
    formattedTransactionDate: momentTransactionDate.format(dateFormatStr),
    price: stripePriceConverter(price)
  };
}

export function transformClean(
  clean,
  serviceTypeData,
  addonsData,
  properties = []
) {
  const {
    serviceDate,
    serviceType,
    additionalService,
    transactions,
    timeframe,
    propertyID,
    ...rest
  } = clean;

  const property = properties.find(item => item.propertyID === propertyID);

  const currentYear = getCurrentYear();
  const momentServiceDate = fromIgnoredUtc(moment(serviceDate));
  const yearNum = momentServiceDate.year();
  const dateFormatStr = currentYear === yearNum ? 'MMM DD' : 'MMM DD, YYYY';

  const dateNum = momentServiceDate.date();
  const monthNum = momentServiceDate.month() + 1;
  const formattedDate = momentServiceDate.format(dateFormatStr || 'MMM DD');
  const defaultFormattedDate = momentServiceDate.format('MMM DD, YYYY');
  const formattedDay = momentServiceDate.format('dddd');
  const ymdString = momentServiceDate.format('YYYY-MM-DD');

  const newItem = {
    ...rest,
    serviceType: serviceTypeData[serviceType],
    additionalService: (additionalService || []).map(
      addon => addonsData[addon]
    ),
    serviceDate: dateNum,
    serviceMonth: monthNum,
    serviceYear: yearNum,
    serviceDateTimestamp: momentServiceDate.unix(),
    formattedDate,
    defaultFormattedDate,
    formattedDay,
    momentServiceDate,
    serviceDateYmdString: ymdString,
    transactions: transformTransaction(transactions),
    timeframe,
    timeframeTitle: TIME_FRAME_TITLES[timeframe],
    property,
    propertyID
  };
  return newItem;
}

export function transformSchedule(scheduleItem) {
  const {
    row,
    col,
    serviceType,
    addons,
    serviceDate,
    rescheduleItem
  } = scheduleItem;
  const { timeframe } = serviceDate || {};

  const { rescheduleDate, skip } = rescheduleItem || {};
  const result = {
    week: +row + 1,
    day: +col + 1,
    serviceType,
    additionalService: addons,
    timeframe
  };

  if (skip) {
    result.skip = skip;
  } else if (rescheduleDate) {
    result.rescheduleDate = rescheduleDate.momentDate;
  }

  return result;
}

export function transformSubscription(
  subscription,
  serviceTypeData,
  addonsData,
  properties = []
) {
  const {
    startDate,
    nextScheduleStartDate,
    cycle,
    resumeDate,
    lastBill,
    discontinueDate
  } = subscription;

  const currentYear = getCurrentYear();

  let momentNextScheduleStartDate = fromIgnoredUtc(
    moment(nextScheduleStartDate)
  );

  const currentCycleStartDate = momentNextScheduleStartDate
    .clone()
    .subtract(cycle.length, 'week');

  let momentResumeDate;
  let yearNum;
  if (resumeDate) {
    momentResumeDate = fromIgnoredUtc(moment(resumeDate));
    yearNum = momentResumeDate.year();
  }

  let momentDiscontinueDate;
  if (discontinueDate) {
    momentDiscontinueDate = fromIgnoredUtc(moment(discontinueDate));
  }

  const dateFormatStr = currentYear === yearNum ? 'MM/DD' : 'MM/DD/YYYY';

  const { date: lastBillDate } = lastBill || {};

  const newBill = lastBillDate ? { ...lastBill } : undefined;

  if (newBill) {
    newBill.formattedLastBillDate = moment(lastBillDate).format(dateFormatStr);
  }

  const cycleInfo = cycle.map(item =>
    transformClean(item, serviceTypeData, addonsData, properties)
  );

  let disabledCycleLength = 0;
  let finalServiceDate;
  if (discontinueDate) {
    const discontinueWeek = momentDiscontinueDate.week();
    const nextCycleStartWeek = momentNextScheduleStartDate.week();

    const diff = discontinueWeek - nextCycleStartWeek;

    if (diff <= 0) {
      for (let i = cycleInfo.length - 1; i >= 0; i -= 1) {
        const item = cycleInfo[i];
        const { momentServiceDate } = item;
        if (momentServiceDate.isBefore(momentDiscontinueDate)) {
          finalServiceDate = momentServiceDate;
          disabledCycleLength = cycleInfo.length - i - 1;
          break;
        }
      }
    } else {
      const offsetWeek = Math.floor(diff / cycleInfo.length) * cycleInfo.length;
      for (let i = cycleInfo.length - 1; i >= 0; i -= 1) {
        const item = cycleInfo[i];
        const { momentServiceDate } = item;
        const calcMomentDate = momentServiceDate
          .clone()
          .add(offsetWeek, 'week');
        if (calcMomentDate.isBefore(momentDiscontinueDate)) {
          finalServiceDate = calcMomentDate;
          disabledCycleLength = cycleInfo.length - i - 1;
          break;
        }
      }
    }
  }

  return {
    formattedStartingDate: fromIgnoredUtc(moment(startDate)).format(
      dateFormatStr
    ),
    formattedNextScheduleStartDate: momentNextScheduleStartDate.format(
      dateFormatStr
    ),
    formattedResumeDate: fromIgnoredUtc(moment(resumeDate)).format(
      dateFormatStr
    ),
    formattedFinalServiceDate: finalServiceDate
      ? finalServiceDate.format(dateFormatStr)
      : '',
    disabledCycleLength,
    formattedDiscontinueDate: discontinueDate
      ? momentDiscontinueDate.format(dateFormatStr)
      : '',
    cycleInfo,
    ...subscription,
    lastBill: newBill,
    currentCycleStartDate
  };
}

export function transformAvailableDate(itemDate, isReverse) {
  const { day: itemDay, morning, afternoon } = itemDate || {};

  const currentYear = getCurrentYear();
  const momentDate = fromIgnoredUtc(moment(itemDay));
  const ymdString = momentDate.format('YYYY-MM-DD');
  const itemYear = momentDate.year();
  const dateFormatStr =
    currentYear === itemYear ? 'dddd, MMMM DD' : 'dddd, MMMM DD, YYYY';

  const availableWindow = [];
  if (morning && afternoon) {
    availableWindow.push(TIME_FRAME.ANYTIME);
  }
  if (morning) {
    availableWindow.push(TIME_FRAME.MORNING);
  }
  if (afternoon) {
    availableWindow.push(TIME_FRAME.AFTERNOON);
  }

  if (isReverse) {
    if (morning || afternoon) {
      return null;
    }
    return {
      momentDate: momentDate,
      formattedDate: momentDate.format(dateFormatStr),
      timestamp: momentDate.unix()
    };
  }

  if (!morning && !afternoon) {
    return null;
  }

  return {
    momentDate: momentDate,
    formattedDate: momentDate.format(dateFormatStr),
    ymdString,
    timestamp: momentDate.unix(),
    availableWindow,
    timeframe: availableWindow[0]
  };
}

export function transformBlockedDays(blockedDays) {
  const daysSet = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5
  };

  return blockedDays.map(item => daysSet[item.toLowerCase()]);
}

export function transfromPropertyToAddress(property) {
  const { address, state, city, zipcode } = property || {};

  if (!state || !city) {
    return 'All Properties';
  }
  const fullAddress = `${address}, ${city}, ${state} ${zipcode}`;
  return fullAddress;
}
